import * as dateFns from 'date-fns';
import z from 'zod';
import holidaysJson from '../holidays.json';

const holidays = holidaysJson.map((h) =>
  dateFns.format(
    dateFns.parse(h['対象日付（yyyyMMdd）'], 'yyyyMMdd', new Date()),
    'yyyy-MM-dd'
  )
);

export const isHoliday = (date: Date) => {
  return holidays.includes(dateFns.format(date, 'yyyy-MM-dd'));
};

export const isWeekend = (date: Date) => {
  const dayOfWeek = dateFns.getDay(date);
  return dayOfWeek === 0 || dayOfWeek === 6;
};

export const addBusinessDays = (start: Date, days: number) => {
  let result = new Date(start);
  let added = 0;

  while (added < days) {
    result = dateFns.addDays(result, 1);

    if (!isWeekend(result) && !isHoliday(result)) {
      added++;
    }
  }

  return result;
};

export const SendEmailRequestSchema = z.object({
  email: z.string().email()
});

export type SendEmailRequestValidation = ReturnType<
  (typeof SendEmailRequestSchema)['safeParse']
>;

export const ConfirmEmailRequestSchema = z.object({
  token: z.string()
});

export const genders = ['F', 'M', 'U'] as const;

export type Gender = (typeof genders)[number];

export const genderNames: Record<Gender, string> = {
  F: '女性',
  M: '男性',
  U: '不詳'
};

export const GenderSchema = z.enum(genders);

export const BirthdateSchema = z
  .object({
    year: z.coerce.number().int(),
    month: z.coerce.number().int(),
    day: z.coerce.number().int()
  })
  .refine(({ year, month, day }) => {
    const date = new Date(year, month - 1, day);
    const minDate = dateFns.subYears(dateFns.startOfYear(new Date()), 120);
    return dateFns.isValid(date) && dateFns.isAfter(date, minDate);
  });

export const isKatakana = (s: string) => s.match(/^[\u30A0-\u30FF\s]*$/);

export const yesOrNoChoices = ['notSelected', 'no', 'yes'] as const;

export type YesOrNo = (typeof yesOrNoChoices)[number];

export const yesOrNoChoicesNames: Record<YesOrNo, string> = {
  notSelected: '未選択',
  no: '無',
  yes: '有'
};

export const movementChoices = [
  'notSelected',
  'walk',
  'cane',
  'wheelchair'
] as const;

export type MovementChoice = (typeof movementChoices)[number];

export const movementChoicesNames: Record<MovementChoice, string> = {
  notSelected: '未選択',
  walk: '歩行',
  cane: '松葉杖',
  wheelchair: '車椅子'
};

export const athleteTimeSlots = ['noPreference', 'thirteen'] as const;

export type AthleteTimeSlot = (typeof athleteTimeSlots)[number];

export const athleteTimeSlotNames: Record<AthleteTimeSlot, string> = {
  noPreference: '希望なし',
  thirteen: '13:00'
};

export const reservationTimeSlots = [
  'noPreference',
  'nineToEleven',
  'elevenToThirteen',
  'thirteenToFifteen',
  'fifteenToSeventeen'
] as const;

export type ReservationTimeSlot = (typeof reservationTimeSlots)[number];

export const reservationTimeSlotNames: Record<ReservationTimeSlot, string> = {
  noPreference: '希望なし',
  nineToEleven: '9:00 ~ 11:00',
  elevenToThirteen: '11:00 ~ 13:00',
  thirteenToFifteen: '13:00 ~ 15:00',
  fifteenToSeventeen: '15:00 ~ 17:00'
};

export const medicalHistoryChoices = ['U', 'Y', 'N'] as const;

export type MedicalHistoryChoice = (typeof medicalHistoryChoices)[number];

export const medicalHistoryChoicesNames: Record<MedicalHistoryChoice, string> =
  {
    U: '不明',
    Y: '有',
    N: '無'
  };

export const MedicalHistoryChoiceSchema = z.enum(medicalHistoryChoices);

export const departments = [
  'Undefined',
  'Cardiology',
  'Ophthalmology',
  'ObstetricsAndGynecology',
  'Pediatrics',
  'Dermatology',
  'Urology',
  'OtolaryngologyOrHeadAndNeckSurgery',
  'Orthopedics',
  'PsychiatryAndNeurology',
  'DentalAndOralSurgery',
  'RadiationTherapy',
  'Anesthesiology',
  'Neurosurgery',
  'PlasticSurgery',
  'Hematology',
  'CardiovascularSurgery',
  'PrimaryCareOrEmergencyDepartment',
  'DiagnosticRadiology',
  'Gastroenterology',
  'ImmunologyAndCollagenDiseases',
  'Nephrology',
  'MedicalOncology',
  'GastrointestinalSurgery',
  'BreastSurgery',
  'HepatoBiliaryPancreaticAndTransplantSurgery',
  'PediatricSurgery',
  'Neurology',
  'EarlyMedicalDevelopment',
  'RespiratoryMedicine',
  'ThoracicSurgery',
  'CellTherapy',
  'iPSCellClinicalApplicationDepartment',
  'DiabetesEndocrinologyAndNutrition',
  'PalliativeCare',
  'Pathology',
  'RehabilitationDepartment',
  'InfectionControlDepartment', // 新規追加
  'GeneticCounselingDepartment', // 新規追加
  'OutpatientCancerCareDepartment', // 新規追加
  'KampoMedicineDepartment', // 新規追加
  'RheumatologyCenter', // 新規追加
  'GeriatricMedicalUnit', // 新規追加
  'DaySurgery' // 新規追加
] as const;

export type Department = (typeof departments)[number];

export const departmentNames: Record<Department, string> = {
  Undefined: '未選択',
  Cardiology: '循環器内科',
  Ophthalmology: '眼科',
  ObstetricsAndGynecology: '産科婦人科',
  Pediatrics: '小児科',
  Dermatology: '皮膚科',
  Urology: '泌尿器科',
  OtolaryngologyOrHeadAndNeckSurgery: '耳鼻咽喉科・頭頸部外科',
  Orthopedics: '整形外科',
  PsychiatryAndNeurology: '精神科神経科',
  DentalAndOralSurgery: '歯科口腔外科',
  RadiationTherapy: '放射線治療科',
  Anesthesiology: '麻酔科',
  Neurosurgery: '脳神経外科',
  PlasticSurgery: '形成外科',
  Hematology: '血液内科',
  CardiovascularSurgery: '心臓血管外科',
  PrimaryCareOrEmergencyDepartment: '初期診療・救急科',
  DiagnosticRadiology: '放射線診断科',
  Gastroenterology: '消化器内科',
  ImmunologyAndCollagenDiseases: '免疫・膠原病内科',
  Nephrology: '腎臓内科',
  MedicalOncology: '腫瘍内科',
  GastrointestinalSurgery: '消化管外科',
  BreastSurgery: '乳腺外科',
  HepatoBiliaryPancreaticAndTransplantSurgery: '肝胆膵・移植外科',
  PediatricSurgery: '小児外科',
  Neurology: '脳神経内科',
  EarlyMedicalDevelopment: '早期医療開発科',
  RespiratoryMedicine: '呼吸器内科',
  ThoracicSurgery: '呼吸器外科',
  CellTherapy: '細胞療法科',
  iPSCellClinicalApplicationDepartment: 'iPS細胞臨床開発部',
  DiabetesEndocrinologyAndNutrition: '糖尿病・内分泌・栄養内科',
  PalliativeCare: '緩和医療科',
  Pathology: '病理診断科', // リスト不備により追加
  RehabilitationDepartment: 'リハビリテーション科', // リスト不備により追加
  InfectionControlDepartment: '感染制御部', // 新規追加
  GeneticCounselingDepartment: '遺伝子診療部', // 新規追加
  OutpatientCancerCareDepartment: '外来がん診療部', // 新規追加
  KampoMedicineDepartment: '漢方診療ユニット', // 新規追加
  RheumatologyCenter: 'リウマチセンター', // 新規追加
  GeriatricMedicalUnit: '高齢者医療ユニット', // 新規追加
  DaySurgery: 'デイサージャリー' // 新規追加
};

export const ReservationDesiredSlotSchema = z.object({
  date: z.coerce.date().refine((date) => {
    if (!dateFns.isValid(date)) {
      return false;
    }
    if (dateFns.isBefore(date, addBusinessDays(dateFns.startOfToday(), 7))) {
      return false;
    }

    if (dateFns.isAfter(date, dateFns.addYears(dateFns.startOfToday(), 1))) {
      return false;
    }

    return !isWeekend(date) && !isHoliday(date);
  }, '今日から少なくとも7営業日後の日付を入力してください (土日祝・12/29～1/3を除く)'),
  time: z.enum(reservationTimeSlots)
});

export const AthleteDesiredSlotSchema = z.object({
  date: z.coerce.date().refine((date) => {
    if (!dateFns.isValid(date)) {
      return false;
    }

    if (dateFns.isAfter(date, dateFns.addYears(dateFns.startOfToday(), 1))) {
      return false;
    }

    return !isWeekend(date) && !isHoliday(date);
  }, '土日祝・12/29～1/3以外の日付を入力してください'),
  time: z.enum(athleteTimeSlots)
});

export const ReservationRequestSchema = z.object({
  token: z.string(),
  patientId: z
    .string()
    .refine((id) => id.match(/^\d{8}$/), '8桁の数字を入力してください'),
  patientName: z.string().min(1),
  patientFurigana: z
    .string()
    .min(1)
    .refine(isKatakana, '全角カタカナで入力してください'),
  patientGender: GenderSchema,
  patientBirthdate: BirthdateSchema,
  patientContact: z
    .string()
    .refine(
      (contact) => contact.match(/^\d{10,11}$/),
      '10～11桁の数字を入力してください'
    ),
  reservedDepartment: z.enum(departments),
  reservedDate: z.coerce.date().refine((date) => dateFns.isValid(date)),
  desiredSlot1: ReservationDesiredSlotSchema,
  desiredSlot2: ReservationDesiredSlotSchema.optional(),
  desiredSlot3: ReservationDesiredSlotSchema.optional(),
  desiredRequest: z.string().optional()
});

export type ReservationRequestValidation = ReturnType<
  (typeof ReservationRequestSchema)['safeParse']
>;

const AthleteRegistrationInformationSchema = z.object({
  motherNameFurigana: z
    .string()
    .trim()
    .min(1)
    .refine(isKatakana, '全角カタカナで入力してください'),
  contact: z
    .string()
    .refine(
      (contact) => contact.match(/^\d{10,11}$/),
      '10～11桁の数字を入力してください'
    )
});

export const AthleteInformationSchema = z
  .object({
    name: z.string().trim().min(1),
    nameFurigana: z
      .string()
      .trim()
      .min(1)
      .refine(isKatakana, '全角カタカナで入力してください'),
    gender: GenderSchema,
    birthDate: BirthdateSchema,
    boneFractureStatus: z.enum(yesOrNoChoices),
    walkingStatus: z.enum(movementChoices),
    fixationInjuredStatus: z.enum(yesOrNoChoices),
    injuryName: z.string().min(1),
    medicalHistory: MedicalHistoryChoiceSchema,
    medicalNumber: z
      .string()
      .refine(
        (medicalNumber) => medicalNumber.match(/^\d{8}$/),
        '8桁の数字を入力してください'
      )
      .optional(),
    registrationInformation: AthleteRegistrationInformationSchema.partial()
  })
  .superRefine((data, ctx) => {
    if (data.medicalNumber) {
      return; // there is no need to check registration information if medical number is provided
    }

    const result = AthleteRegistrationInformationSchema.safeParse(
      data.registrationInformation
    );
    if (result.error) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          ...issue,
          path: ['registrationInformation', ...issue.path]
        });
      }
    }
  });

export type AthleteInformationValidation = ReturnType<
  (typeof AthleteInformationSchema)['safeParse']
>;

export type AthleteInformation = z.infer<typeof AthleteInformationSchema>;

export const AthletesRegistrationRequestSchema = z.object({
  token: z.string(),
  groupName: z.string().min(1),
  groupNameFurigana: z
    .string()
    .min(1)
    .refine(isKatakana, '全角カタカナで入力してください'),
  representativeName: z.string().min(1),
  representativeNameFurigana: z
    .string()
    .min(1)
    .refine(isKatakana, '全角カタカナで入力してください'),
  phoneNumber: z
    .string()
    .refine(
      (contact) => contact.match(/^\d{10,11}$/),
      '10～11桁の数字を入力してください'
    ),
  athletes: AthleteInformationSchema.array().min(1),
  desiredSlot1: AthleteDesiredSlotSchema,
  desiredSlot2: AthleteDesiredSlotSchema.optional(),
  notes: z.string().optional()
});

export type AthletesRegistrationRequestValidation = ReturnType<
  (typeof AthletesRegistrationRequestSchema)['safeParse']
>;
